import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { Error404 } from './components/astro/404';
import { LoadingComponent } from './components/shared-ui/loading-component';
import { DefaultCatchBoundary } from './components/start/DefaultCatchBoundary';
import { shouldRetryQuery } from '@listening/frontend/query/query-error';
import { deLocalizeUrl, localizeUrl } from '@/paraglide/runtime';
import { routeTree } from './routeTree.gen';
// Stale-chunk recovery: the import registers the `vite:preloadError` listener
// (side effect) and exposes the two halves of `reportRouteError` below —
// the reload attempt, and the predicate that says whether the resulting
// failure is recovery noise. See stale-chunk.ts.
import {
  handleRouterStaleChunk,
  shouldSuppressStaleChunkError,
} from './stale-chunk';
import { parseSearch, stringifySearch } from './lib/search-params';

// Every error that reaches `DefaultCatchBoundary` — the user-visible
// "Something went wrong!" screen — for both render and loader failures.
// TanStack strips `notFound` and redirect errors before calling this, so a
// 404 is never reported as an exception. Attempting the stale-chunk reload
// first is what makes the suppression correct: a reload that was scheduled
// is navigating away and its chunk error is recovery noise, while one the
// rate limit refused leaves the user stuck on the boundary and must report.
function reportRouteError(error: unknown): void {
  if (error instanceof Error) handleRouterStaleChunk(error);
  if (shouldSuppressStaleChunkError(error)) return;
  Sentry.captureException(error);
}

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
      },
    },
  });

  const router = createTanStackRouter({
    routeTree,
    context: {
      queryClient,
      cloudfrontRegion: undefined,
      isFreePlanCountry: undefined,
      analyticsBootstrap: undefined,
      edgeFlags: undefined,
    },
    defaultPreload: 'intent',
    defaultPendingComponent: LoadingComponent,
    defaultPendingMinMs: 0,
    notFoundMode: 'fuzzy',
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <Error404 />,
    rewrite: {
      input: ({ url }) => deLocalizeUrl(url),
      output: ({ url }) => localizeUrl(url),
    },
    defaultOnCatch: reportRouteError,
    parseSearch,
    stringifySearch,
  });

  setupRouterSsrQueryIntegration({ router, queryClient });

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
